import InsightLayout from "../../components/InsightLayout";
import FoerderHinweis from "../../components/FoerderHinweis";

const SUBTITLE = `DPIA und FRIA — zwei Buchstaben-Kürzel, die im KI-Compliance-Kontext ständig verwechselt werden. Die Verwechslung ist teuer: Wer glaubt, eine Datenschutz-Folgenabschätzung nach DSGVO erfülle automatisch die Grundrechte-Folgenabschätzung des AI Act, baut eine Compliance-Lücke ein, die bei der ersten Prüfung auffliegt. Hier ist, wann eine DPIA für KI-Systeme Pflicht ist, was die FRIA zusätzlich verlangt — und warum die „Absorptionsklausel" des AI Act ein Dokumentations-Trick ist, keine Gleichsetzung.`;

const DsgvoArt35Ki = () => {
  return (
    <InsightLayout
      title="DSGVO Art. 35 und KI: Wann eine Datenschutz-Folgenabschätzung Pflicht wird — und warum sie nicht die FRIA ersetzt"
      subtitle={SUBTITLE}
      metaDescription="DPIA und FRIA werden verwechselt, und das ist teuer. Wann eine Datenschutz-Folgenabschätzung für KI Pflicht wird und was die FRIA zusätzlich verlangt."
      publishedDate="2026-05-26"
      updatedDate="2026-09-03"
      readingTime="11 Min Lesezeit"
      category="Regulatorik"
    >
      <p className="text-lg">
        Es gibt zwei Abkürzungen, die im KI-Compliance-Kontext ständig
        durcheinandergeworfen werden: <strong>DPIA</strong>{" "}
        (Datenschutz-Folgenabschätzung nach DSGVO Art. 35) und{" "}
        <strong>FRIA</strong> (Fundamental Rights Impact Assessment nach AI
        Act Art. 27). Ich erlebe in Mandaten regelmäßig, dass beide für
        dasselbe gehalten werden — oder dass angenommen wird, das eine
        erledige das andere.
      </p>

      <p>
        Diese Verwechslung ist teuer. Wer glaubt, eine DPIA erfülle
        automatisch die FRIA-Pflicht, baut eine Compliance-Lücke ein, die
        bei der ersten ernsthaften Prüfung auffliegt. Dieser Artikel
        sortiert die beiden Instrumente sauber — und erklärt die
        „Absorptionsklausel", die fast jeder falsch versteht.
      </p>

      <p>
        Vorweg die wichtigste Botschaft, falls Sie nur einen Satz mitnehmen:{" "}
        <strong>Die DPIA-Pflicht für KI-Systeme gilt heute, nicht erst mit
        dem AI Act.</strong> Sie folgt aus der DSGVO und ist seit 2018
        scharf. Wer KI mit personenbezogenen Daten einsetzt und auf den AI
        Act wartet, ist möglicherweise schon jetzt im Verzug.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die DPIA: Wann sie nach DSGVO Art. 35 zur Pflicht wird
      </h2>

      <p>
        Eine Datenschutz-Folgenabschätzung ist immer dann erforderlich, wenn
        eine Verarbeitung personenbezogener Daten <em>voraussichtlich ein
        hohes Risiko für die Rechte und Freiheiten natürlicher
        Personen</em> zur Folge hat. Das ist die Generalklausel. Konkreter
        wird es durch drei Regel-Trigger in Art. 35 Abs. 3:
      </p>

      <ol className="list-decimal pl-6 space-y-3 my-4">
        <li>
          <strong>Systematische und umfassende Bewertung persönlicher
          Aspekte</strong> auf Basis automatisierter Verarbeitung
          (einschließlich Profiling), die als Grundlage für Entscheidungen
          mit Rechtswirkung oder ähnlich erheblicher Beeinträchtigung dient.
        </li>
        <li>
          <strong>Umfangreiche Verarbeitung besonderer Kategorien</strong>{" "}
          (Art. 9 — Gesundheit, Religion, politische Meinung, biometrische
          Daten zur Identifikation u. a.) oder von Daten über
          strafrechtliche Verurteilungen.
        </li>
        <li>
          <strong>Systematische umfangreiche Überwachung</strong> öffentlich
          zugänglicher Bereiche.
        </li>
      </ol>

      <p>
        Zusätzlich hat die deutsche Datenschutzkonferenz (DSK) eine{" "}
        <strong>„Muss-Liste"</strong> mit 16 Fallgruppen für den
        nicht-öffentlichen Bereich veröffentlicht, bei denen eine DPIA in
        Deutschland verpflichtend ist. Darunter: KI-Einsatz zur Verhaltens-
        oder Leistungsbewertung von Beschäftigten, umfangreiche Verarbeitung
        biometrischer Daten, Scoring-Verfahren. (Nicht zu verwechseln mit den
        neun Risiko-Kriterien der früheren Art.-29-Datenschutzgruppe, bei denen
        bereits das Erfüllen von zwei Kriterien regelmäßig eine DPIA auslöst.)
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Warum KI-Systeme fast immer einen DPIA-Trigger auslösen
      </h2>

      <p>
        Hier liegt der Punkt, den viele Mittelständler unterschätzen: Die
        typischen KI-Use-Cases im Unternehmen treffen fast zwangsläufig einen
        der drei Trigger. Drei Beispiele aus der Praxis:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          <strong>KI-gestützte Bewerber-Vorauswahl</strong> → systematische
          Bewertung persönlicher Aspekte mit Entscheidungsrelevanz (Trigger
          1). DPIA-Pflicht.
        </li>
        <li>
          <strong>KI im Kunden-Scoring / Bonitätsprüfung</strong> →
          automatisierte Entscheidung mit erheblicher Beeinträchtigung
          (Trigger 1, oft auch Art. 22). DPIA-Pflicht.
        </li>
        <li>
          <strong>KI-Analyse von Mitarbeiter-Kommunikation oder
          -Leistung</strong> → DSK-Muss-Liste, plus
          mitbestimmungsrechtliche Implikationen (§ 87 BetrVG). DPIA-Pflicht.
        </li>
      </ul>

      <p>
        Die Faustregel, die ich Mandanten gebe: Wenn ein KI-System
        personenbezogene Daten verarbeitet UND auch nur indirekt
        Entscheidungen über Menschen beeinflusst, gehen Sie von einer
        DPIA-Pflicht aus, bis das Gegenteil bewiesen ist. Die umgekehrte
        Annahme ist riskanter als der Aufwand der Prüfung.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die FRIA: Was der AI Act zusätzlich verlangt
      </h2>

      <p>
        Die Fundamental Rights Impact Assessment nach AI Act Art. 27 ist ein
        anderes Instrument mit anderem Fokus. Während die DPIA{" "}
        <em>datenzentriert</em> ist — was wird erhoben, wie gespeichert, ist
        die Verarbeitung rechtmäßig — ist die FRIA{" "}
        <em>menschenzentriert</em>: Behandelt das System Menschen fair?
        Erzeugt es systematische Benachteiligung? Haben Betroffene einen
        echten Weg, Entscheidungen anzufechten?
      </p>

      <p>
        Die FRIA bewertet ausdrücklich Grundrechte, die eine DPIA gar nicht
        im Blick hat: Nicht-Diskriminierung, Meinungsfreiheit, Zugang zur
        Justiz, das Recht auf gute Verwaltung, Menschenwürde. Das ist ein
        deutlich breiteres Feld als reiner Datenschutz.
      </p>

      <p>
        Wichtig für die Praxis: Die FRIA-Pflicht trifft{" "}
        <strong>Deployer von Hochrisiko-KI-Systemen</strong> — und ihre
        Geltung hängt an der Hochrisiko-Deadline. Die wurde durch den Digital
        Omnibus (politische Einigung im Mai, in Kraft seit 27. Juli 2026) von
        August 2026 auf den 2. Dezember 2027 verschoben (Details dazu im{" "}
        <a
          href="/insights/eu-ai-act-2026"
          className="text-[#C5A66A] underline"
        >
          Artikel zum EU AI Act
        </a>
        ). Die DPIA-Pflicht dagegen gilt unabhängig davon, schon heute.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die Absorptionsklausel — der Punkt, den fast alle falsch verstehen
      </h2>

      <p>
        Artikel 27(4) des AI Act enthält eine Regelung, die in der Praxis
        massiv missverstanden wird. Sinngemäß: Wenn für ein
        KI-System bereits eine DPIA nach DSGVO durchgeführt wurde, gilt die
        FRIA für die dort bereits abgedeckten Pflichten als erfüllt.
      </p>

      <p>
        Viele lesen das als: <em>„DPIA gemacht = FRIA erledigt."</em> Das ist
        falsch und gefährlich. Die korrekte Lesart: Die Absorptionsklausel
        ist ein Mechanismus der <strong>Dokumentations-Ökonomie</strong>,
        nicht der inhaltlichen Gleichsetzung. Sie erspart Ihnen, dieselbe
        Risikobeschreibung zweimal zu schreiben — sie erspart Ihnen nicht
        die zusätzlichen FRIA-Inhalte.
      </p>

      <p>
        Was eine Standard-DPIA (auch nach dem neuen EDPB-Template-Entwurf vom
        April 2026, der sich zum Redaktionszeitpunkt noch in öffentlicher
        Konsultation befand) <strong>nicht</strong> abdeckt und die FRIA
        zusätzlich verlangt:
      </p>

      <ul className="list-disc pl-6 space-y-2 my-4">
        <li>
          Die Bewertung von <strong>Zeitraum und Häufigkeit</strong> der
          System-Nutzung
        </li>
        <li>
          Das <strong>Mapping aller betroffenen Personengruppen</strong> —
          auch über den Kreis hinaus, dessen personenbezogene Daten
          verarbeitet werden
        </li>
        <li>
          Die Analyse <strong>spezifischer Schadensrisiken</strong> nach Art.
          27(1)(d) — insbesondere für Grundrechte jenseits des Datenschutzes
        </li>
        <li>
          Die Bewertung von <strong>Nicht-Diskriminierung und
          Fairness</strong> als eigenständige Dimension
        </li>
      </ul>

      <p>
        Der vernünftige Deployer schreibt also nicht „DPIA = FRIA". Er
        erstellt eine <strong>integrierte Bewertung</strong>, in der jede von
        jeder Verordnung geforderte Komponente eine explizite Antwort
        bekommt. Wo sich Inhalte überschneiden, wird einmal geschrieben und
        referenziert. Wo die FRIA über die DPIA hinausgeht, wird ergänzt.
        Das ist der Unterschied zwischen sauberer Compliance und einem
        Dokument, das bei der Prüfung durchfällt.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Was die EDPB-Guidance noch klären wird
      </h2>

      <p>
        Stand Mai 2026 arbeitet der Europäische Datenschutzausschuss (EDPB)
        gemeinsam mit dem AI Office der Kommission an gemeinsamen Leitlinien
        zum Zusammenspiel von AI Act und Datenschutzrecht. Diese Guidance
        ist angekündigt, aber noch nicht final veröffentlicht.
      </p>

      <p>
        Praktische Konsequenz: In einigen Detailfragen — etwa der genauen
        Abgrenzung, welche FRIA-Elemente durch eine DPIA als abgedeckt
        gelten — gibt es noch keine endgültige behördliche Festlegung. Wer
        jetzt baut, sollte konservativ vorgehen: lieber beide Bewertungen
        vollständig und sauber getrennt dokumentieren, als sich auf eine
        großzügige Auslegung der Absorptionsklausel zu verlassen, die später
        kassiert werden könnte.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Konkrete Schritte für Ihr Unternehmen
      </h2>

      <p>
        <strong>Eins — DPIA-Triage jetzt.</strong> Für jedes KI-System mit
        Personenbezug: Trifft einer der drei Art.-35-Trigger oder ein
        Punkt der DSK-Muss-Liste zu? Wenn ja, ist die DPIA überfällig — sie
        gilt seit 2018, nicht erst mit dem AI Act. Das ist die dringendste
        Lücke.
      </p>

      <p>
        <strong>Zwei — Hochrisiko-Einordnung parallel.</strong> Prüfen Sie,
        ob das System unter Annex III des AI Act fällt (siehe{" "}
        <a
          href="/insights/eu-ai-act-2026"
          className="text-[#C5A66A] underline"
        >
          EU-AI-Act-Artikel
        </a>
        ). Wenn ja, kommt die FRIA-Pflicht mit der verschobenen Deadline
        hinzu — aber die Vorbereitung beginnt jetzt, nicht 2027.
      </p>

      <p>
        <strong>Drei — Integrierte Vorlage bauen.</strong> Statt zweier
        getrennter Dokumente: eine integrierte Assessment-Vorlage, die beide
        Anforderungen abbildet und klar markiert, welche Komponente aus
        welcher Verordnung stammt. Das spart Aufwand und macht Prüfungen
        nachvollziehbar.
      </p>

      <p>
        <strong>Vier — Datenschutzbeauftragten früh einbinden.</strong> Die
        DPIA ist nach Art. 35 Abs. 2 mit dem Datenschutzbeauftragten
        abzustimmen. Bei KI-Projekten gehört diese Einbindung an den Anfang,
        nicht ans Ende — sonst baut man Wochen an einer Architektur, die der
        DSB anschließend in Frage stellt.
      </p>

      <p>
        <strong>Fünf — Dokumentation als laufenden Prozess verstehen.</strong>{" "}
        Sowohl DPIA als auch FRIA sind keine Einmal-Dokumente. Bei
        wesentlichen Änderungen am System (neue Datenquellen, geänderter
        Verwendungszweck, neues Modell) sind sie zu aktualisieren. Bauen Sie
        das als Prozess, nicht als Projekt.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die ehrliche Einordnung
      </h2>

      <p>
        DPIA und FRIA sind kein bürokratischer Selbstzweck. Sie sind, richtig
        gemacht, ein strukturiertes Nachdenken darüber, was ein KI-System mit
        Menschen tut, bevor es scharf geschaltet wird. Genau dieses
        Nachdenken fehlt in den meisten gescheiterten KI-Projekten — nicht
        aus Boshaftigkeit, sondern weil niemand die Frage gestellt hat.
      </p>

      <p>
        Der häufigste Fehler im Mittelstand ist nicht, die falsche Bewertung
        zu schreiben. Es ist, gar keine zu schreiben — im Glauben, das
        betreffe nur Großkonzerne, oder im Vertrauen darauf, dass der
        AI-Act-Aufschub auch die Datenschutz-Pflichten verschiebt. Tut er
        nicht. Die DSGVO gilt unverändert, und ihre Aufsichtsbehörden haben
        2026 ihre KI-Durchsetzung erkennbar verschärft.
      </p>

      <p>
        Wer das ernst nimmt, hat am Ende nicht nur weniger Bußgeldrisiko,
        sondern auch die besseren KI-Systeme — weil die Pflicht zur
        Folgenabschätzung genau die Schwachstellen sichtbar macht, die ein
        System sonst erst im Produktivbetrieb offenbart.
      </p>

      <p className="mt-12 text-sm text-gray-600 italic">
        Stand der Angaben: 26. Mai 2026. Die gemeinsame EDPB/Kommissions-
        Guidance zum Zusammenspiel von AI Act und Datenschutzrecht war zum
        Redaktionszeitpunkt angekündigt, aber nicht final veröffentlicht.
        Primäre Quellen:{" "}
        <a
          href="https://datenschutz-grundverordnung.eu/dsgvo/art-35-dsgvo/"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          DSGVO Art. 35
        </a>{" "}
        ·{" "}
        <a
          href="https://artificialintelligenceact.eu/article/27/"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          AI Act Art. 27 (FRIA)
        </a>{" "}
        ·{" "}
        <a
          href="https://www.edpb.europa.eu/our-work-tools/our-documents/topic/artificial-intelligence_en"
          className="text-[#C5A66A] underline"
          target="_blank"
          rel="noopener"
        >
          EDPB AI-Dokumente
        </a>
        . Dieser Artikel ist keine Rechtsberatung. Für die konkrete Bewertung
        Ihrer Systeme ziehen Sie Ihren Datenschutzbeauftragten und
        qualifizierten Rechtsrat hinzu.
      </p>
      <FoerderHinweis
        text="Datenschutzkonforme KI-Einführung — inklusive externer Begleitung der Folgenabschätzung — lässt sich in vielen Ländern als Digitalisierungsvorhaben fördern. Die Programme unterscheiden sich je Bundesland deutlich."
        links={[
          { href: "https://foerdermittel.daaniyalkhan.com/programme/digitalisierung/hessen/", label: "Digitalisierungsförderung Hessen" },
          { href: "https://foerdermittel.daaniyalkhan.com/wissen/de-minimis-erklaert/", label: "Guide: De-minimis einfach erklärt" },
          { href: "https://foerdermittel.daaniyalkhan.com/", label: "Kostenloser Förder-Check" },
        ]}
      />

    </InsightLayout>
  );
};

export default DsgvoArt35Ki;
